/* eslint-disable */
import React from "react";
import PropTypes from "prop-types";
import {
  Grid,
  CardContent,
  Typography,
  Box,
  makeStyles,
} from "@material-ui/core";
import InfoTooltipButton from "./InfoTooltipButton";

import Card from "components/Card/Card.js";
import CardHeader from "../../components/Card/CardHeader";
import CardText from "../../components/Card/CardText";

import black from "../../assets/img/lights/black_circle.png";
import red from "../../assets/img/lights/red_circle.png";
import yellow from "../../assets/img/lights/yellow_circle.png";
import green from "../../assets/img/lights/green_circle.png";

import { useTranslation } from "react-i18next";
import PermissionGuideDialog from "./PermissionGuideDialog";

/**
 * Estilos locales del resumen del semáforo.
 *
 * Objetivos de esta versión:
 * - mantener la estética actual
 * - dejar la cabecera realmente responsive
 * - mantener visible el icono de información
 * - evitar posicionados absolutos innecesarios
 * - hacer las cápsulas más estables en móvil
 */
const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(1),
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(2),
    },
  },

  /**
   * CardText actúa como la pastilla visual del header.
   * Forzamos un layout horizontal para título + icono.
   */
  headerBadge: {
    display: "block",
    width: "fit-content",
    maxWidth: "100%",
  },

  /**
   * Contenido interno de la pastilla del header.
   * Aquí sí conviven el título y el botón de ayuda.
   */
  headerContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 0,
    maxWidth: "100%",
  },

  headerTitleWrap: {
    minWidth: 0,
    flex: 1,
  },

  cardTitle: {
    margin: 0,
    fontWeight: 400,
    color: "#fff",
    fontSize: "1rem",
    wordBreak: "break-word",
    [theme.breakpoints.up("md")]: {
      fontSize: "1.2rem",
    },
  },

  headerActions: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  },

  perms: {
    width: "100%",
    alignItems: "center",
    marginLeft: 0,
    marginRight: 0,

    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(1),
    },
  },

  /**
   * Cápsula de permiso clicable.
   * Quitamos restricciones demasiado agresivas en móvil.
   */
  perm: {
    maxWidth: 320,
    margin: "0 auto",
    width: "100%",
    marginBottom: theme.spacing(2),
    cursor: "pointer",
    transition: "box-shadow 0.2s ease, transform 0.2s ease, opacity 0.2s ease",
    "&:hover": {
      boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
      transform: "translateY(-1px)",
    },
    "&:focus-visible": {
      outline: "2px solid #4f749f",
      outlineOffset: 2,
    },

    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
      marginBottom: theme.spacing(1.5),
    },
  },

  permLoading: {
    opacity: 0.7,
    cursor: "wait",
  },

  permCard: {
    padding: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    minWidth: 0,
  },

  permName: {
    minWidth: 0,
    flex: 1,
    wordBreak: "break-word",
    lineHeight: 1.35,

    [theme.breakpoints.down("sm")]: {
      fontSize: "0.85rem",
    },
  },

  lightFrame: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(1),
    backgroundColor: "#333",
    borderRadius: 32,
    width: "100%",
    maxWidth: 320,
    margin: "0 auto",

    [theme.breakpoints.down("sm")]: {
      maxWidth: 260,
      borderRadius: 24,
    },
  },

  lightMoji: {
    width: 40,
    height: 40,
    margin: theme.spacing(0.5),

    [theme.breakpoints.up("md")]: {
      width: 55,
      height: 55,
      margin: theme.spacing(1),
    },

    [theme.breakpoints.down("sm")]: {
      width: 34,
      height: 34,
      margin: theme.spacing(0.5),
    },
  },

  lightSmall: {
    width: 18,
    height: 18,
    flexShrink: 0,

    [theme.breakpoints.up("md")]: {
      width: 25,
      height: 25,
    },
  },

  tooltip: {
    fontSize: "0.9rem",
    padding: "12px 16px",
    maxWidth: 320,
  },

  arrow: {},

  /**
   * El icono ya no vive sobre fondo transparente del CardHeader,
   * sino dentro de la propia pastilla turquesa.
   */
  infoIconWrapper: {
    width: 30,
    height: 30,
    minWidth: 30,
    minHeight: 30,
    padding: 0,
    color: "#fff",
    backgroundColor: "rgba(255, 255, 255, 0.16)",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
    flexShrink: 0,
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.24)",
    },
  },
  
  infoIcon: {
    width: 18,
    height: 18,
    fontSize: 18,
    display: "block",
    position: "relative",
    top: "-2px",
  },

  titleMain: {
    wordBreak: "break-word",
  },

  scoreText: {
    wordBreak: "break-word",
  },

  summaryText: {
    wordBreak: "break-word",
  },
}));

/**
 * Color del título principal según el resultado del semáforo.
 */
const getTitleColor = (color) => {
  switch (color) {
    case "rojo":
      return "#f44336";
    case "amarillo":
      return "#ff9800";
    case "verde":
      return "#4caf50";
    default:
      return "#333";
  }
};

/**
 * Normaliza la clave del permiso para que siempre sea compatible
 * con los JSON de i18n, independientemente de si viene como:
 *
 * - ACCESS_FINE_LOCATION
 * - android.permission.ACCESS_FINE_LOCATION
 */
const normalizePermissionKey = (value) =>
  String(value || "").replace(/^android\.permission\./, "");

/**
 * Inserta espacios de ruptura invisibles después de "_"
 * para que los nombres largos de permisos no rompan el layout.
 */
const formatPermissionLabel = (value) =>
  normalizePermissionKey(value).replace(/_/g, "_\u200B");

/**
 * Calcula la puntuación de alineación entre permisos detectados
 * en código y permisos documentados.
 */
const computePermissionScore = (perms) => {
  if (!perms || perms.length === 0) return "?";

  const totalCodePerms = perms.filter((p) => p.count_code > 0).length;
  if (totalCodePerms === 0) return "?";

  const missingCount = perms.filter(
    (p) => p.count_code > 0 && (!p.count_docs || p.count_docs === 0)
  ).length;

  if (missingCount === 0) return 100;

  let baseScore = ((totalCodePerms - missingCount) / totalCodePerms) * 100;

  if (missingCount > 1) baseScore = Math.min(baseScore, 40);
  else if (missingCount === 1) baseScore = Math.min(baseScore, 60);

  return Math.round(baseScore);
};

/**
 * Componente principal del resumen/overview del semáforo.
 */
export const Overview = ({ permissionsSummary, permissions, color, infoKey }) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation("search");

  const [selectedPermission, setSelectedPermission] = React.useState(null);
  const [guideOpen, setGuideOpen] = React.useState(false);
  const [guideLoading, setGuideLoading] = React.useState(false);

  const alignmentScore = computePermissionScore(permissionsSummary);

  let perms = [];
  if (permissionsSummary && permissionsSummary.length > 0) {
    perms = permissionsSummary;
  } else if (permissions) {
    perms = Object.entries(permissions)
      .filter(([_, data]) => data.status === "dangerous")
      .map(([permission, data]) => ({
        permission: normalizePermissionKey(permission),
        ...data,
        count_docs: null,
      }));
  }

  const tabPairs = [];
  for (let i = 0; i < perms.length; i += 2) {
    tabPairs.push([perms[i], perms[i + 1] || null]);
  }

  const tooltipKey = infoKey ? `overview-tooltip-${infoKey}` : null;
  const tooltipText = tooltipKey ? t(tooltipKey, { defaultValue: "" }) : "";

  let lights = [black, black, black];
  let titleKey;
  let summaryKey;
  let emoji = "";

  switch (color) {
    case "rojo":
      lights[2] = red;
      titleKey = "overview-level-danger-title";
      summaryKey = "overview-level-danger-summary";
      break;
    case "amarillo":
      lights[1] = yellow;
      titleKey = "overview-level-caution-title";
      summaryKey = "overview-level-caution-summary";
      break;
    case "verde":
      lights[0] = green;
      titleKey = "overview-level-safe-title";
      summaryKey = "overview-level-safe-summary";
      break;
    default:
      emoji = "⚠️";
      titleKey = "overview-level-notfound-title";
      summaryKey = "overview-level-notfound-summary";
      break;
  }

  /**
   * Abre la guía didáctica del permiso.
   */
  const handleOpenPermissionGuide = async (permission) => {
    const permissionKey = normalizePermissionKey(permission);
    if (!permissionKey || guideLoading) return;

    setGuideLoading(true);

    try {
      await i18n.loadNamespaces("permissionsGuide");
      setSelectedPermission(permissionKey);
      setGuideOpen(true);
    } catch (error) {
      console.error("No se pudo cargar el namespace permissionsGuide:", error);
    } finally {
      setGuideLoading(false);
    }
  };

  const handleClosePermissionGuide = () => {
    setGuideOpen(false);
    setSelectedPermission(null);
  };

  const handlePermissionKeyDown = (event, permission) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleOpenPermissionGuide(permission);
    }
  };

  return (
    <>
      <Card>
        <CardHeader color="primary" icon>
          <CardText color="primary" className={classes.headerBadge}>
            <div className={classes.headerContent}>
              <div className={classes.headerTitleWrap}>
                <h4 className={classes.cardTitle}>{t("overview-card-title")}</h4>
              </div>

              {tooltipText ? (
                <div className={classes.headerActions}>
                  <InfoTooltipButton
                    title={tooltipText}
                    placement="right"
                    tooltipClasses={{ tooltip: classes.tooltip, arrow: classes.arrow }}
                    iconButtonClassName={classes.infoIconWrapper}
                    iconClassName={classes.infoIcon}
                  />
                </div>
              ) : null}
            </div>
          </CardText>
        </CardHeader>

        <div className={classes.container}>
          {/* Semáforo visual */}
          <div className={classes.lightFrame}>
            {lights.map((light, index) => (
              <img
                key={index}
                src={light}
                alt=""
                aria-hidden="true"
                className={classes.lightMoji}
              />
            ))}
          </div>

          {/* Título, score y resumen */}
          <CardContent>
            <Typography
              variant="h5"
              align="center"
              className={classes.titleMain}
              style={{ color: getTitleColor(color) }}
            >
              {emoji} {t(titleKey)} {emoji}
            </Typography>

            <Typography
              align="center"
              className={classes.scoreText}
              style={{ color: getTitleColor(color) }}
            >
              {t("overview-score-label", { score: alignmentScore })}
            </Typography>

            <Box mt={2}>
              <Typography align="center" className={classes.summaryText}>
                {t(summaryKey)}
              </Typography>
            </Box>
          </CardContent>

          {/* Cápsulas de permisos */}
          <Grid className={classes.perms}>
            {tabPairs.map((pair, index) => (
              <Grid container spacing={2} alignItems="stretch" key={index}>
                {pair.map(
                  (tab, idx) =>
                    tab && (
                      <Grid item xs={12} md={6} key={idx}>
                        <Card
                          className={`${classes.perm} ${
                            guideLoading ? classes.permLoading : ""
                          }`}
                          onClick={() => handleOpenPermissionGuide(tab.permission)}
                          onKeyDown={(event) =>
                            handlePermissionKeyDown(event, tab.permission)
                          }
                          role="button"
                          tabIndex={0}
                          aria-busy={guideLoading ? "true" : "false"}
                          aria-label={normalizePermissionKey(tab.permission)}
                        >
                          <CardContent className={classes.permCard}>
                            <Typography className={classes.permName}>
                              {formatPermissionLabel(tab.permission)}
                            </Typography>

                            <img
                              className={classes.lightSmall}
                              alt=""
                              aria-hidden="true"
                              src={
                                tab.count_docs != null
                                  ? tab.count_docs < 1
                                    ? red
                                    : green
                                  : black
                              }
                            />
                          </CardContent>
                        </Card>
                      </Grid>
                    )
                )}
              </Grid>
            ))}
          </Grid>
        </div>
      </Card>

      <PermissionGuideDialog
        open={guideOpen}
        permissionKey={selectedPermission}
        onClose={handleClosePermissionGuide}
      />
    </>
  );
};

Overview.propTypes = {
  permissionsSummary: PropTypes.array,
  permissions: PropTypes.object,
  color: PropTypes.string,
  infoKey: PropTypes.string,
};